import {getLinoOptionBase, getLinoOptionColor, getLinoOptionColorKey, linoColorLabel, linoOptionLabel} from '../../print-options';
import {logger} from '../../../logger';
import React from 'react';
import {observable, action, computed, makeObservable, toJS} from 'mobx';
import {observer} from 'mobx-react';
import s from '../../s';
import {linoStore} from './lino-store';
import ModalSimple from '../../../global/modal-simple';
import {Portal} from '../../../util';
import {allowsAutomatedDocSizes} from '../doc-sizes-and-categories/document-sizing';
import {
    isIconLinoProduct,
    linoArtworkSrc,
    linoCategories,
    linoColorOptionsCount,
    linoColorSelectShown,
    linoHasMultipleColors,
    linoIconUi,
    linoRemovable,
    linoTextGroups,
    prepareLinos,
    removeLino,
    restoreOriginalLino,
    selectLino,
    selectLinoDocSize,
    setLinoColor,
    uncategorizedLinos,
} from '../../selection/lino';
import {disclosurePanelClass} from '../disclosure/panel-class';

@observer
export default class Lino extends React.Component {
    @observable collapsed: boolean = true;
    @observable panelOpen: boolean = false;
    @observable init_linos: boolean = false;
    @observable orig_lino_name: string = '';
    @observable orig_lino_title: string = '';
    @observable lino_display_name: string = '';
    @observable lino_color: string = '';
    @observable lino_select_text: string = '';
    @observable IsMultipleColors: boolean = false;
    isIcon: boolean = false;
    icon_ui: boolean = false;

    constructor(props) {
        super(props);
        this.collapseToggle = this.collapseToggle.bind(this);
        this.initLinos = this.initLinos.bind(this);
        this.orig = this.orig.bind(this);
        this.changeLinoColor = this.changeLinoColor.bind(this);
        this.lino_remove = this.lino_remove.bind(this);
        s.debug_lino = !s.is_production;

        if (s.si.ProductLinoSelectionText && s.si.ProductLinoSelectionText.toLocaleLowerCase().includes('school') && s.currentGradSchoolID < 1) {
            this.lino_select_text = 'School';
        } else {
            this.lino_select_text = s.si.ProductLinoSelectionText ? s.si.ProductLinoSelectionText : 'Embossed Text';
        }

        this.isIcon = isIconLinoProduct();
        if (this.isIcon && s.prod.ProductTypeID !== 83) {
            this.lino_select_text = 'Embossed Text';
        }

        this.icon_ui = linoIconUi();

        makeObservable(this);
        if (s.debug_lino) logger.log('lino.tsx:constructor:s.linoChoices.options', toJS(s.linoChoices.options));
        if (s.debug_lino) logger.log('lino.tsx:constructor:s.linoChoices.options', toJS(s.linoChoices.options));
    }

    @action
    collapseToggle = async () => {
        this.collapsed = !this.collapsed;
    };

    @action
    initLinos = async () => {
        this.orig_lino_name = JSON.parse(JSON.stringify(s.prod.orig_pcs.Base_LinoName));
        this.orig_lino_title = s.prod.LinoDisplayName;
        this.lino_display_name = s.prod.LinoDisplayName;
        this.lino_color = s.prod.LinoColor;
        this.init_linos = true;

        this.IsMultipleColors = linoHasMultipleColors();

        // the display titles (_title) and the order of the choices (selection/lino.ts)
        prepareLinos(this.icon_ui);

        if (s.debug_lino) logger.log('lino.tsx:initLinos:s.si.ProductDocSizes', toJS(s.si.ProductDocSizes));
        if (s.debug_lino) logger.log('lino.tsx:s.prod.linos:initLinos', toJS(s.prod.linos));
    };

    @action
    orig = async () => {
        restoreOriginalLino(this.orig_lino_name);
        this.lino_display_name = s.prod.LinoDisplayName;
        this.lino_color = s.prod.LinoColor;
    };

    @action
    changeLinoProd = async (_id: number) => {
        // the store side (grad school, document sizes, pricing, lino_changed) is shared with the redesign: selection/lino.ts
        const m = selectLino(_id);
        if (m) {
            this.lino_display_name = m.title;
            this.lino_color = m.desc4;
        }

        if (s.currentGradSchoolID > 0) {
            this.lino_select_text = s.si.ProductLinoSelectionText ? s.si.ProductLinoSelectionText : 'Graduate School';
        } else {
            let _hasGSOptions = (s.si.ProductDocSizes ?? []).filter(x => x.GradSchoolID > 0 || x.ApplyToGradSchools);
            if (_hasGSOptions && _hasGSOptions.length > 0) {
                this.lino_select_text = s.si.ProductLinoSelectionText && s.si.ProductLinoSelectionText.toLocaleLowerCase().includes('school') ? 'School' : 'Graduate School';
            } else {
                this.lino_select_text = s.si.ProductLinoSelectionText && s.si.ProductLinoSelectionText.toLocaleLowerCase().includes('school') ? 'School' : 'Embossed Text';
            }
        }
    };

    @action
    changeLinoProd_ModalResult = async (_docSizeId: number = -1) => {
        selectLinoDocSize(_docSizeId);
    };

    @action
    togglePanel = () => {
        if (!this.canCollapse) return;
        this.panelOpen = !this.panelOpen;
    };

    @computed get canCollapse(): boolean {
        return s.ps.is_caf || window.innerWidth < 960;
    }

    @computed get getOptionsCount(): number {
        return linoColorOptionsCount();
    }

    // Linos that belong to a frame-part-category (fpc_id > 0), grouped by category.
    // Each group renders as its own dropdown titled with the category name (fpc_name).
    // Linos without a category (fpc_id <= 0) are left to the existing display logic.
    @computed get linoCategories(): {id: number; name: string; items: any[]}[] {
        return linoCategories();
    }

    // Linos that do NOT belong to a category (fpc_id <= 0) — these drive the
    // existing (non-category) lino display.
    @computed get uncategorizedLinos(): any[] {
        return uncategorizedLinos();
    }

    // Uncategorized linos grouped by text. When the same text comes in more than one
    // color, the dropdown lists each text once and a color dropdown picks the color.
    @computed get linoTextGroups() {
        return linoTextGroups();
    }

    @computed get currentLinoTextGroup() {
        const groups = this.linoTextGroups.groups;
        return groups.find(g => g.items.some(m => m.id === s.preferredLinoId(s.ps.lino_id))) ?? groups[0];
    }

    // Switch text, keeping the current color when the new text has it; otherwise its first color.
    changeLinoText = async (key: string) => {
        const group = this.linoTextGroups.groups.find(g => g.key === key);
        if (!group) {
            return;
        }
        const current = this.currentLinoTextGroup?.items.find(m => m.id === s.preferredLinoId(s.ps.lino_id));
        const m = (current && group.items.find(x => getLinoOptionColorKey(x) === getLinoOptionColorKey(current))) || group.items[0];
        await this.handleLinoDropdown(m.id.toString());
    };

    getLinoColor = (m: any): string => getLinoOptionColor(m);

    getLinoBase = (m: any): string => getLinoOptionBase(m);

    @computed get currentLinoColor(): string {
        const c = (s.ps.lino_dyf_color || '').toString().trim().toLowerCase();
        return c === 'black' || c === 'silver' || c === 'gold' ? c : 'gold';
    }

    @computed get linoCategoryColors(): string[] {
        const set = new Set<string>();
        for (const cat of this.linoCategories) {
            for (const m of cat.items) {
                set.add(this.getLinoColor(m));
            }
        }
        return ['gold', 'silver', 'black'].filter(c => set.has(c));
    }

    @action
    changeLinoCategoryColor = async (e: React.ChangeEvent<HTMLSelectElement>) => {
        const color = e.target.value.toString().trim().toLowerCase();
        s.ps.lino_dyf_color = color;

        // If a categorized lino is selected, switch to the same item in the new color.
        const current = s.linoChoices.options.filter(x => x.id === s.preferredLinoId(s.ps.lino_id) && x.fpc_id > 0)[0];
        if (current) {
            const base = this.getLinoBase(current);
            const sibling = s.linoChoices.options.filter(x => x.fpc_id === current.fpc_id && this.getLinoBase(x) === base && this.getLinoColor(x) === color)[0];
            if (sibling) {
                await this.handleLinoDropdown(sibling.id.toString());
                return;
            }
        }

        s.ui.update_pricing = true;
        s.ui.doRender = true;
        s.pulse();
    };

    @action
    changeLinoColor = async (e: React.ChangeEvent<HTMLSelectElement>) => {
        setLinoColor(e.target.value);
    };

    @action
    lino_remove = async () => {
        removeLino();
    };

    async handleLinoDropdown(id: string) {
        // console.log('id', id);
        if (id === '-1') {
            await this.orig();
        } else {
            await this.changeLinoProd(+id);
        }
    }

    render() {
        if (!s.ui.show_cust || !s.prod || !s.prod.linos) {
            return;
        }
        if (!this.init_linos) {
            this.initLinos();
        }

        // A mat change can replace the selected lino without running this component's
        // click handler. Keep both headings in step with the artwork actually selected.
        const selected = !s.prod.IsCAF && !s.ps.lino_is_dyf && s.prod.ProductID !== 88937 ? s.prod.linos.find(lino => lino.id === s.ps.lino_id) : undefined;
        const original = s.preferredLinoId(s.prod.LinoID) === s.preferredLinoId(s.ps.lino_id);
        const displayName = selected ? (original ? s.prod.LinoDisplayName : selected.title) : this.lino_display_name;
        const color = selected ? (original ? s.prod.LinoColor : selected.desc4) : this.lino_color;

        if (s.si.StoreID === 205164) {
            linoStore.getData();
        }

        return (
            <div
                className="gray-box lino-prod"
                style={{padding: 0}}
            >
                <div className={this.canCollapse ? '' : ''}>
                    <div
                        style={{
                            padding: '0 12px',
                            position: 'relative',
                        }}
                    >
                        <div
                            role={this.canCollapse ? 'button' : undefined}
                            tabIndex={this.canCollapse ? 0 : undefined}
                            onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    e.currentTarget.click();
                                }
                            }}
                            className="d-none d-xl-block toggle-bs"
                            onClick={this.togglePanel}
                            aria-expanded={this.canCollapse ? this.panelOpen : undefined}
                            aria-controls={this.canCollapse ? 'lino-inner' : undefined}
                        >
                            <div className="cust-desktop-head">
                                <span>{this.lino_select_text}: </span>
                                {this.icon_ui && (
                                    <span>{s.ps.lino_id > 0 ? `${(displayName || '').includes(color || '') ? displayName : displayName + (original ? ` ${color}` : '')}` : ''}</span>
                                )}
                                {!this.icon_ui && (
                                    <span>
                                        {s.ps.lino_id > 0 ? displayName : ''}
                                        {this.IsMultipleColors && !(displayName || '').includes(color || '') ? ` - ${color}` : ''}
                                    </span>
                                )}
                            </div>
                        </div>

                        <div
                            role="button"
                            tabIndex={0}
                            onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    this.collapsed = !this.collapsed;
                                    e.currentTarget.click();
                                }
                            }}
                            onClick={() => {
                                this.collapsed = !this.collapsed;
                                this.togglePanel();
                            }}
                            className="d-block d-xl-none toggle-bs"
                            aria-expanded={this.canCollapse ? this.panelOpen : undefined}
                            aria-controls={this.canCollapse ? 'lino-inner' : undefined}
                        >
                            <div className="cust-flex-head">
                                <div>
                                    <div
                                        className="header-std flexer cust-flex2"
                                        style={{paddingRight: '0.25rem'}}
                                    >
                                        {this.lino_select_text}
                                    </div>
                                    <div
                                        className="cust-flex2"
                                        style={{marginTop: 4}}
                                    >
                                        {this.icon_ui && (
                                            <span>
                                                {s.ps.lino_id > 0 ? displayName : ''}
                                                {s.ps.lino_id > 0 && original ? ` ${color}` : ''}
                                            </span>
                                        )}
                                        {!this.icon_ui && (
                                            <span>
                                                {s.ps.lino_id > 0 ? displayName : ''}
                                                {this.IsMultipleColors && !(displayName || '').includes(color || '') ? ` - ${color}` : ''}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div>
                                    {this.canCollapse && this.collapsed && s.ps.lino_id > 0 && (
                                        <img
                                            src={s.ps.css.lino_src}
                                            className="th-h"
                                            alt={`${this.lino_select_text} Thumbnail`}
                                            style={{
                                                backgroundColor: `#${s.ui.color_mat1}`,
                                                width: 120,
                                                marginTop: 6,
                                            }}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div
                        id="lino-inner"
                        className={disclosurePanelClass(this.canCollapse, this.panelOpen)}
                        aria-label="Select Lino"
                        style={{padding: '0 12px'}}
                    >
                        {s.prod.IsCAF && this.linoCategories.length > 0 && this.linoCategoryColors.length > 0 && (
                            <div className="form-floating cust-lino-select">
                                <select
                                    id="lino-cat-color"
                                    className="form-select"
                                    aria-label={`Select ${this.lino_select_text} color`}
                                    value={this.currentLinoColor}
                                    onChange={this.changeLinoCategoryColor}
                                >
                                    {this.linoCategoryColors.map(c => (
                                        <option
                                            key={c}
                                            value={c}
                                        >
                                            {c.charAt(0).toUpperCase() + c.slice(1)}
                                        </option>
                                    ))}
                                </select>
                                <label htmlFor="lino-cat-color">{this.lino_select_text} Color</label>
                            </div>
                        )}

                        {linoColorSelectShown() && (
                            <div>
                                <div className="form-floating cust-lino-select">
                                    <select
                                        id="lino-color"
                                        className="form-select"
                                        aria-label="Select lino color"
                                        onChange={this.changeLinoColor}
                                    >
                                        {s.ps.ins_black_allowed && (
                                            <option
                                                value="black"
                                                selected={s.ps.lino_dyf_color === 'black'}
                                            >
                                                Black
                                            </option>
                                        )}
                                        {s.ps.ins_gold_allowed && (
                                            <option
                                                value="gold"
                                                selected={s.ps.lino_dyf_color === 'gold'}
                                            >
                                                Gold
                                            </option>
                                        )}
                                        {s.ps.ins_silver_allowed && (
                                            <option
                                                value="silver"
                                                selected={s.ps.lino_dyf_color === 'silver'}
                                            >
                                                Silver
                                            </option>
                                        )}
                                    </select>
                                    <label htmlFor="lino-color">{this.lino_select_text} Color</label>
                                </div>
                            </div>
                        )}

                        <div
                            id="lino-inner2"
                            style={{gap: 2}}
                        >
                            {((s.prod.LinoID > 0 && s.linoChoices.options.length < 3 && s.prod.ProductID !== 88937) || (s.prod.LinoID > 0 && s.linoChoices.options.length === 1 && s.prod.ProductID !== 88937)) && (
                                <div
                                    className={`cust-entity${s.preferredLinoId(s.prod.LinoID) === s.preferredLinoId(s.ps.lino_id) ? ' active' : ''}`}
                                    style={{
                                        margin: s.linoChoices.options.length === 1 && s.hasDYFLinoNonProduct ? '8px 4px' : '4px 2px',
                                        borderRadius: '0.375rem',
                                        height: s.linoChoices.options.length === 1 && s.hasDYFLinoNonProduct ? 58 : 46,
                                        lineHeight: s.linoChoices.options.length === 1 && s.hasDYFLinoNonProduct ? '58px' : '46px',
                                        paddingTop: 0,
                                        paddingBottom: 0,
                                        width: 'auto',
                                    }}
                                    role="button"
                                    aria-pressed={s.preferredLinoId(s.prod.LinoID) === s.preferredLinoId(s.ps.lino_id)}
                                    tabIndex={0}
                                    onClick={this.orig}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            this.orig();
                                        }
                                    }}
                                    aria-label={`Select original ${this.lino_select_text.toLowerCase()}`}
                                >
                                    {this.icon_ui ? `${s.prod.LinoDisplayName} ${s.prod.LinoColor}` : Portal?.PortalID === 0 ? this.orig_lino_title : 'Select your program'}
                                </div>
                            )}

                            {(s.linoChoices.options.length === 2 || (s.prod.LinoID < 1 && s.linoChoices.options.length === 1)) &&
                                s.prod.ProductID !== 88937 &&
                                s.linoChoices.options
                                    .filter(m => !m.isBase && !(m.fpc_id > 0))
                                    .map((m, i) => (
                                        <div
                                            key={i}
                                            className={`cust-entity${s.preferredLinoId(s.ps.lino_id) === m.id ? ' active' : ''}`}
                                            role="button"
                                            aria-pressed={s.preferredLinoId(s.ps.lino_id) === m.id}
                                            style={{
                                                margin: '4px 2px',
                                                borderRadius: '0.375rem',
                                                height: 46,
                                                lineHeight: '46px',
                                                paddingTop: 0,
                                                paddingBottom: 0,
                                                width: 'auto',
                                            }}
                                            onClick={e => this.changeLinoProd(m.id)}
                                            onKeyDown={e => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    this.changeLinoProd(m.id);
                                                }
                                            }}
                                            aria-label={`Select ${m.title} ${this.lino_select_text.toLowerCase()}`}
                                            tabIndex={0}
                                        >
                                            {this.icon_ui ? m.title.trim() : m.title.replace(s.si.InstName, '').trim()}
                                        </div>
                                    ))}

                            {s.linoChoices.options.length !== 1 && ((s.linoChoices.options.length > 2 && s.hasSomeProductLino) || s.prod.ProductID === 88937) && (s.si.StoreID === 205164 || s.prod.ProductID === 88937 || this.uncategorizedLinos.length > 0) && (
                                <div
                                    className=""
                                    style={{width: s.hasDYFLinoNonProduct ? 'inherit' : '100%', margin: 0, maxWidth: s.si.StoreID === 205164 ? 400 : this.linoTextGroups.hasColors ? '100%' : 300}}
                                >
                                    {s.si.StoreID === 205164 ? (
                                        <div style={{display: 'flex', flexDirection: 'column', gap: 10}}>
                                            <select
                                                id="lino-prod-select-1"
                                                className="form-select"
                                                style={{paddingTop: 10, paddingBottom: 10, height: '100%', width: 'auto'}}
                                                aria-label={`Select ${this.lino_select_text.toLowerCase()}`}
                                                onChange={e => {
                                                    linoStore.setSelected(e.target.value);
                                                    this.handleLinoDropdown(linoStore.desc2OptionsFiltered.length ? linoStore.desc2OptionsFiltered[0].id.toString() : linoStore.selectedDesc1);
                                                }}
                                            >
                                                <option
                                                    key={-1}
                                                    value={-1}
                                                >
                                                    {this.icon_ui ? `${s.prod.LinoDisplayName} ${s.prod.LinoColor}` : Portal?.PortalID === 0 ? this.lino_display_name + (this.isIcon ? ` - ${this.lino_color}` : '') : 'Select your program'}
                                                </option>
                                                {linoStore.desc1Options.map((i, index) => (
                                                    <option
                                                        key={index}
                                                        value={i}
                                                    >
                                                        {i}
                                                    </option>
                                                ))}
                                            </select>

                                            {linoStore.selectedDesc1 && linoStore.selectedDesc1 !== '-1' && (
                                                <select
                                                    id="lino-prod-select-2"
                                                    className="form-select"
                                                    aria-label={`Select ${this.lino_select_text.toLowerCase()}`}
                                                    style={{paddingTop: 10, paddingBottom: 10, height: '100%', width: 'auto'}}
                                                    onChange={e => this.handleLinoDropdown(e.target.value)}
                                                >
                                                    {linoStore.desc2OptionsFiltered.map((i, index) => (
                                                        <option
                                                            key={index}
                                                            value={i.id}
                                                        >
                                                            {i.desc2}
                                                        </option>
                                                    ))}
                                                </select>
                                            )}
                                        </div>
                                    ) : this.linoTextGroups.hasColors ? (
                                        <div className="cust-lino-row">
                                            <div className="form-floating cust-lino-select cust-lino-text">
                                                <select
                                                    id="lino-prod-select"
                                                    className="form-select"
                                                    aria-label={`Select ${this.lino_select_text}`}
                                                    value={this.currentLinoTextGroup?.key}
                                                    onChange={e => this.changeLinoText(e.target.value)}
                                                >
                                                    {this.linoTextGroups.groups.map(g => (
                                                        <option
                                                            key={g.key}
                                                            value={g.key}
                                                        >
                                                            {g.label}
                                                        </option>
                                                    ))}
                                                </select>
                                                <label htmlFor="lino-prod-select">Select {this.lino_select_text}</label>
                                            </div>
                                            {this.currentLinoTextGroup && this.currentLinoTextGroup.items.length > 1 && (
                                                <div className="form-floating cust-lino-select cust-lino-color">
                                                    <select
                                                        id="lino-prod-color"
                                                        className="form-select"
                                                        aria-label={`Select ${this.lino_select_text} color`}
                                                        value={s.preferredLinoId(s.ps.lino_id)}
                                                        onChange={e => this.handleLinoDropdown(e.target.value)}
                                                    >
                                                        {this.currentLinoTextGroup.items.map(m => (
                                                            <option
                                                                key={m.id}
                                                                value={m.id}
                                                            >
                                                                {linoColorLabel(m)}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <label htmlFor="lino-prod-color">Color</label>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div
                                            className="form-floating cust-lino-select"
                                            style={{width: s.hasDYFLinoNonProduct ? 220 : 'auto', maxWidth: s.hasDYFLinoNonProduct ? 220 : '100%'}}
                                        >
                                            <select
                                                id="lino-prod-select"
                                                className="form-select"
                                                aria-label={`Select ${this.lino_select_text}`}
                                                style={{width: s.hasDYFLinoNonProduct ? 220 : 'auto', maxWidth: s.hasDYFLinoNonProduct ? 220 : '100%'}}
                                                onChange={e => this.handleLinoDropdown(e.target.value)}
                                            >
                                                {s.linoChoices.options
                                                    .filter(m => !(m.fpc_id > 0))
                                                    .map((m, i) => (
                                                        <option
                                                            key={i}
                                                            value={m.id}
                                                        >
                                                            {linoOptionLabel(m, m._title ?? m.title)}
                                                        </option>
                                                    ))}
                                            </select>
                                            <label htmlFor="lino-prod-select">Select {this.lino_select_text}</label>
                                        </div>
                                    )}
                                </div>
                            )}

                            {s.si.StoreID !== 205164 &&
                                this.linoCategories.map(cat => {
                                    const items = s.prod.IsCAF ? cat.items.filter(m => this.getLinoColor(m) === this.currentLinoColor) : cat.items;
                                    if (items.length === 0) {
                                        return null;
                                    }
                                    const selectedInCat = items.some(it => it.id === s.preferredLinoId(s.ps.lino_id));
                                    return (
                                        <div
                                            key={cat.id}
                                            style={{width: s.hasDYFLinoNonProduct ? 'inherit' : '100%', margin: 0, maxWidth: 300}}
                                        >
                                            <div
                                                className="form-floating cust-lino-select"
                                                style={{width: s.hasDYFLinoNonProduct ? 220 : 'auto', maxWidth: s.hasDYFLinoNonProduct ? 220 : '100%'}}
                                            >
                                                <select
                                                    id={`lino-fpc-${cat.id}`}
                                                    className="form-select"
                                                    aria-label={`Select ${cat.name}`}
                                                    value={selectedInCat ? s.preferredLinoId(s.ps.lino_id) : -1}
                                                    style={{width: s.hasDYFLinoNonProduct ? 220 : 'auto', maxWidth: s.hasDYFLinoNonProduct ? 220 : '100%'}}
                                                    onChange={e => this.handleLinoDropdown(e.target.value)}
                                                >
                                                    <option value={-1}>{s.si.auto_grad_lino ? `Original ${this.lino_select_text}` : `${cat.name}...`}</option>
                                                    {items.map(m => (
                                                        <option
                                                            key={m.id}
                                                            value={m.id}
                                                        >
                                                            {linoOptionLabel(m, m.title)}
                                                        </option>
                                                    ))}
                                                </select>
                                                <label htmlFor={`lino-fpc-${cat.id}`}>{cat.name}</label>
                                            </div>
                                        </div>
                                    );
                                })}

                            {s.ps.lino_id > 0 && s.prod.ProductID !== 88937 && (
                                <div>
                                    <hr style={{margin: '0.5rem 0'}} />
                                    <div style={{backgroundColor: `#${s.ps.mat1_color}`}}>
                                        <img
                                            className="resp"
                                            src={linoArtworkSrc(Portal?.CDNDomain)}
                                            alt={`Selected ${this.lino_select_text}`}
                                        />
                                    </div>
                                </div>
                            )}

                            {linoRemovable() && (
                                <div
                                    className="ep-toggle text-center"
                                    style={{marginBottom: '1rem'}}
                                >
                                    <hr style={{margin: '0.5rem 0'}} />
                                    <button
                                        type="button"
                                        aria-label={`Remove ${this.lino_select_text.toLowerCase()} from product`}
                                        className="btn btn-secondary"
                                        onClick={this.lino_remove}
                                    >
                                        Remove {this.lino_select_text}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                {allowsAutomatedDocSizes(s.prod, s.si.AutomatedDocSizes) && (
                    <ModalSimple
                        id="modal-lino-doc-size"
                        title={`Select Degree Type - ${this.lino_display_name}`}
                    >
                        <div style={{display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 20}}>
                            {s.si.ProductDocSizes &&
                                s.si.ProductDocSizes.map(i => (
                                    <button
                                        key={i.Id}
                                        className="prod-atc-btn"
                                        style={{backgroundColor: '#00227e', color: '#FFF', textTransform: 'none'}}
                                        onClick={() => this.changeLinoProd_ModalResult(i.Id)}
                                    ></button>
                                ))}
                        </div>
                    </ModalSimple>
                )}
            </div>
        );
    }
}
